import { io } from 'socket.io-client';
import axios from 'axios';
import logger from '@/utils/logger';

function bindBillMailRefreshListener(target) {
  if (!target || target.__billMailRefreshBound) return;
  target.__billMailRefreshBound = true;
  target.on('bill-mail:refresh', (payload) => {
    try {
      window.dispatchEvent(new CustomEvent('bill-mail-refresh', { detail: payload }));
    } catch (_) {
      // ignore
    }
  });
}

let socket = null;
let pingTimer = null;

/**
 * Idle-detection state. These live at module scope so the socket and the
 * idle watcher share the same source of truth across reconnects.
 */
let lastActivityAt = Date.now();
let isActive = true;
let idleCheckTimer = null;
let activityListenersAttached = false;

/** No user input for this long (while the tab is visible) → mark as idle. */
const IDLE_THRESHOLD_MS = 2 * 60 * 1000;

/** How often to recheck idleness. Small relative to the threshold so the
 *  offline transition is timely without spamming the event loop. */
const IDLE_CHECK_INTERVAL_MS = 15 * 1000;

/** Heartbeat cadence — only fires while the user is active. */
const PING_INTERVAL_MS = 45000;

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
  'wheel',
];

function apiBaseWithoutPath() {
  const u = axios.defaults.baseURL || process.env.VUE_APP_API_URL || '';
  return String(u).replace(/\/api\/?$/i, '');
}

function isDocumentHidden() {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function emitState() {
  if (!socket?.connected) return;
  if (isActive) {
    socket.emit('presence:active');
  } else {
    socket.emit('presence:idle', {
      last_active_at: new Date(lastActivityAt).toISOString(),
    });
  }
}

function setActive(next) {
  if (isActive === next) return;
  isActive = next;
  emitState();
}

function markActivity() {
  lastActivityAt = Date.now();
  if (!isActive && !isDocumentHidden()) {
    setActive(true);
  }
}

function checkIdle() {
  if (isDocumentHidden()) {
    setActive(false);
    return;
  }
  if (isActive && Date.now() - lastActivityAt > IDLE_THRESHOLD_MS) {
    setActive(false);
  }
}

function handleVisibilityChange() {
  if (isDocumentHidden()) {
    setActive(false);
  } else {
    markActivity();
  }
}

function attachActivityListeners() {
  if (activityListenersAttached || typeof window === 'undefined') return;
  activityListenersAttached = true;
  const opts = { passive: true };
  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, markActivity, opts));
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
  window.addEventListener('focus', markActivity);
}

function detachActivityListeners() {
  if (!activityListenersAttached || typeof window === 'undefined') return;
  activityListenersAttached = false;
  ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, markActivity));
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
  window.removeEventListener('focus', markActivity);
}

function bindBillNoteListener(target) {
  if (!target || target.__billNoteBound) return
  target.__billNoteBound = true
  target.on('bill-note:new', (payload) => {
    try {
      window.dispatchEvent(new CustomEvent('bill-note-new', { detail: payload }))
    } catch (_) {
      // ignore
    }
  })
}

function bindVendorSubmittedInvoiceListener(target) {
  if (!target) return
  if (target.__vendorSubmittedInvoiceBound) return
  target.__vendorSubmittedInvoiceBound = true
  target.on('vendor-submitted-invoice:new', (payload) => {
    try {
      window.dispatchEvent(
        new CustomEvent('vendor-submitted-invoice', { detail: payload || {} })
      )
    } catch (_) {
      // ignore
    }
  })
}

function bindVendorPresenceListener(target) {
  if (!target || target.__vendorPresenceBound) return
  target.__vendorPresenceBound = true
  target.on('vendor-presence:update', (payload) => {
    try {
      window.dispatchEvent(new CustomEvent('vendor-presence', { detail: payload }))
    } catch (_) {
      // ignore
    }
  })
}

function bindInventoryOnHandListener(target) {
  if (!target || target.__inventoryOnHandBound) return
  target.__inventoryOnHandBound = true
  target.on('inventory:on-hand-updated', (payload) => {
    try {
      window.dispatchEvent(
        new CustomEvent('inventory-on-hand-updated', { detail: payload || {} })
      )
    } catch (_) {
      // ignore
    }
  })
}

/** Clears the session and opens the login page with the "Account Deactivated" notice. */
export function signOutDeactivatedAccount(message = '') {
  disconnectCompanyPresence()
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  // Login.vue reads these and shows the "Account Deactivated" modal.
  sessionStorage.setItem('showApprovalPending', 'true')
  sessionStorage.setItem('accountStatus', 'deactivated')
  if (message) sessionStorage.setItem('accountStatusMessage', message)
  window.location.href = '/login'
}

/**
 * A manager or company owner deactivated this account. The server first sends a countdown
 * warning (AccountDeactivationNotice shows it), then the final sign-out when time is up.
 */
function bindAccountDeactivatedListener(target) {
  if (!target || target.__accountDeactivatedBound) return
  target.__accountDeactivatedBound = true
  target.on('client:account-deactivating', (payload = {}) => {
    window.dispatchEvent(new CustomEvent('client-account-deactivating', { detail: payload }))
  })
  target.on('client:account-deactivation-cancelled', (payload = {}) => {
    window.dispatchEvent(new CustomEvent('client-account-deactivation-cancelled', { detail: payload }))
  })
  target.on('client:account-deactivated', (payload = {}) => {
    signOutDeactivatedAccount(payload.message)
  })
}

function bindRealtimeListeners(target) {
  bindAccountDeactivatedListener(target)
  bindBillMailRefreshListener(target)
  bindBillNoteListener(target)
  bindVendorSubmittedInvoiceListener(target)
  bindVendorPresenceListener(target)
  bindInventoryOnHandListener(target)
}

export function connectCompanyPresence() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  if (socket?.connected) {
    bindRealtimeListeners(socket)
    return socket
  }

  const url = apiBaseWithoutPath();
  if (!url) {
    logger.warn('companyPresenceClient: no API base URL');
    return null;
  }

  attachActivityListeners();
  lastActivityAt = Date.now();
  isActive = !isDocumentHidden();

  if (idleCheckTimer) clearInterval(idleCheckTimer);
  idleCheckTimer = setInterval(checkIdle, IDLE_CHECK_INTERVAL_MS);

  if (socket) {
    socket.auth = { token };
    bindRealtimeListeners(socket);
    socket.connect();
    return socket;
  }

  socket = io(url, {
    path: '/socket.io/',
    transports: ['websocket', 'polling'],
    auth: { token },
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 2000,
  });

  socket.on('presence:update', (payload) => {
    try {
      window.dispatchEvent(new CustomEvent('company-presence', { detail: payload }));
    } catch (_) {
      // ignore
    }
  });

  socket.on('presence:last_seen', (payload) => {
    try {
      window.dispatchEvent(new CustomEvent('company-presence-last-seen', { detail: payload }));
    } catch (_) {
      // ignore
    }
  });

  socket.on('user:notification', (payload) => {
    try {
      window.dispatchEvent(new CustomEvent('company-user-notification', { detail: payload }));
    } catch (_) {
      // ignore
    }
  });

  bindRealtimeListeners(socket);

  socket.on('connect', () => {
    // Re-bind in case this module hot-reloaded while the socket stayed open.
    bindRealtimeListeners(socket);

    // The server treats every fresh socket as idle until told otherwise. We
    // declare our current state immediately so reconnects don't briefly
    // flip an AFK user "online". The state is recomputed from the document
    // visibility right before we emit, so a tab that became hidden during
    // the disconnect window stays offline on reconnect.
    if (isDocumentHidden()) {
      isActive = false;
    }
    emitState();

    if (pingTimer) clearInterval(pingTimer);
    pingTimer = setInterval(() => {
      if (socket?.connected && isActive) socket.emit('presence:ping');
    }, PING_INTERVAL_MS);
  });

  socket.on('disconnect', () => {
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  });

  return socket;
}

export function disconnectCompanyPresence() {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
  if (idleCheckTimer) {
    clearInterval(idleCheckTimer);
    idleCheckTimer = null;
  }
  detachActivityListeners();
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

/** Subscribe Draft Bills Immediate sync for a company (Socket.IO live ticks). */
export function subscribeBillMailLive(companyId) {
  const cid = Number(companyId);
  if (!Number.isFinite(cid)) return;
  const s = connectCompanyPresence();
  if (!s) return;
  const emit = () => s.emit('bill-mail:subscribe', { companyId: cid });
  if (s.connected) emit();
  else s.once('connect', emit);
}

export function unsubscribeBillMailLive(companyId) {
  const cid = Number(companyId);
  if (!Number.isFinite(cid) || !socket) return;
  socket.emit('bill-mail:unsubscribe', { companyId: cid });
}

/** Ask the server for the current vendor portal online list for a company. */
export function requestVendorPresence(companyId) {
  const cid = Number(companyId);
  if (!Number.isFinite(cid)) return;
  const s = connectCompanyPresence();
  if (!s) return;
  const emit = () => s.emit('vendor-presence:request', { companyId: cid });
  if (s.connected) emit();
  else s.once('connect', emit);
}
